import React from 'react';
import {SeriesTermInfo} from "../../models/interfaces/series.interface.ts";
import {SeriesModalAction} from "../../reducers/seriesModalReducer.ts";
import {PiFileCsvLight} from "react-icons/pi";
import SmartIcon from "../SmartIcon.tsx";
import SeriesTerm from "./SeriesTerm.tsx";
import iso6391 from "iso-639-1";

interface SeriesModalDictionaryTabbProps {
  seriesName: string;
  terms: SeriesTermInfo[];
  dispatch: React.ActionDispatch<[action: SeriesModalAction]>
}

const SeriesModalDictionaryTab: React.FC<SeriesModalDictionaryTabbProps> = (
  {
    seriesName,
    terms,
    dispatch
  }: SeriesModalDictionaryTabbProps
) => {
  const [openTerms, setOpenTerms] = React.useState<Record<string, boolean>>({});

  const onTermClick = (termId: string) => {
    setOpenTerms(prev => ({
      ...prev,
      [termId]: !prev[termId]
    }))
  }

  const deleteTerm = (termId: string) => {
    dispatch({
      type: "DELETE_TERM",
      payload: {termId}
    });
    setOpenTerms(prev => {
      const newState = {...prev};
      delete newState[termId];
      return newState;
    });
  }

  const addNewTerm: () => void = (): void => {
    dispatch({
      type: "ADD_NEW_TERM"
    });
  }

  const editTerm: (termId: string, value: string, field: "term" | "sourceLanguage" | "tagType" | "complexityScore" | "meaning") => void = (termId: string, value: string, field: "term" | "sourceLanguage" | "tagType" | "complexityScore" | "meaning") => {
    switch (field) {
      case "term":
        dispatch({
          type: "EDIT_TERM_NAME",
          payload: {termId: termId, newName: value}
        });
        break;
      case "sourceLanguage":
        dispatch({
          type: "EDIT_TERM_SOURCE_LANGUAGE",
          payload: {termId: termId, languageName: value}
        });
        break;
      case "tagType":
        dispatch({
          type: "EDIT_TERM_TAG_TYPE",
          payload: {termId: termId, tagType: value}
        });
        break;
      case "complexityScore":
        dispatch({
          type: "EDIT_TERM_COMPLEXITY_SCORE",
          payload: {termId: termId, score: parseFloat(value)}
        });
        break;
      case "meaning":
        dispatch({
          type: "EDIT_TERM_MEANING",
          payload: {termId: termId, meaning: value}
        });
        break;
    }
  }

  const setTermSynonyms: (termId: string, synonyms: string[]) => void = (termId: string, synonyms: string[]): void => {
    dispatch({
      type: "SET_TERM_SYNONYMS",
      payload: {termId, synonyms}
    });
  }

  const setTermTranslationsKey: (termId: string, oldTargetLanguage: string, newTargetLanguage: string) => void = (termId: string, oldTargetLanguage: string, newTargetLanguage: string): void => {
    dispatch({
      type: "SET_TERM_TRANSLATION_KEY",
      payload: {termId, oldTargetLanguage, newTargetLanguage}
    });

  }

  const setTermTranslations: (termId: string, targetLanguage: string, translation: string) => void = (termId: string, targetLanguage: string, translation: string): void => {
    dispatch({
      type: "SET_TERM_TRANSLATION",
      payload: {termId, targetLanguage, translation}
    });
  }

  const removeTermTranslations: (termId: string, targetLanguage: string) => void = (termId: string, targetLanguage: string): void => {
    dispatch({
      type: "REMOVE_TERM_TRANSLATION",
      payload: {termId, targetLanguage}
    });
  }

  const exportCSV = () => {
    if (!terms || terms.length === 0 || terms.every((term: SeriesTermInfo) => term.term === "")) return;

    const headers: string[] = [
      'Source Language',
      'Term',
      'Tag Type',
      'Other Tag',
      'Synonyms',
      'Localization Complexity Score',
      'Meaning',
      'Translations'
    ];
    // iso6391.getName(term.sourceLanguage)
    const rows: Array<string[]> = terms.filter((term: SeriesTermInfo) => term.term !== "").map((term: SeriesTermInfo) => [
      iso6391.getName(term.sourceLanguage),
      term.term,
      term.tagType,
      term.otherTag,
      term.synonyms.join('; '),
      term.localizationComplexityScore.toString(),
      term.meaning,
      Object.entries(term.translations)
        .map(([lang, translation]: [string, string]) => `${iso6391.getName(lang)}:${translation}`)
        .join('; ')
    ]);

    const csvContent = [headers, ...rows]
      .map(e => e.map((field: string) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${seriesName}_terms.csv`);
    link.click();
  }


  return (
    <div>
      <div className="flex items-center justify-between w-full">
        <div className="h4">Dictionary Terms</div>

        <div className="flex items-center gap-2">
          {(terms.length > 0 && !terms.every((term: SeriesTermInfo) => term.term === "")) && (
            <div
              className="flex items-center gap-2 p-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
              onClick={exportCSV}
            >
              <SmartIcon name={PiFileCsvLight} className="h-5 w-5"/>
              Export CSV
            </div>
          )}
          <div
            className="p-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
            onClick={addNewTerm}
          >
            + Add Term
          </div>
        </div>
      </div>
      <div className="max-h-[500px] overflow-y-auto pr-4  mt-4">
        {terms.map((term: SeriesTermInfo, index: number) => (
          <SeriesTerm
            key={term.termId}
            term={term}
            isOpen={openTerms[term.termId]}
            onCharacterClick={onTermClick}
            termIndex={index}
            editTerm={editTerm}
            setTermSynonyms={setTermSynonyms}
            setTermTranslationsKey={setTermTranslationsKey}
            setTermTranslations={setTermTranslations}
            removeTermTranslations={removeTermTranslations}
            deleteTerm={deleteTerm}
          />
        ))}
      </div>

    </div>
  )
}

export default SeriesModalDictionaryTab;